import parse from 'html-react-parser';
import { useSearchParams } from 'react-router-dom';
import _ from 'lodash';
import { useTranslation } from 'react-i18next';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Article,
  SearchCategoryResultItem,
  useGetApiCategoryTreeQuery,
} from '../../redux/store/api/api';
import CustomListGroup from '../lists/CustomListGroup';
import { getArticleUrl } from '../../shared/urlBuilder';
import {
  searchKeywordUrlParam,
  selectedSearchResultCategory,
} from '../../shared/constants';
import { ArticleType } from '../../shared/enums';
import { getArticleIcon } from '../../shared/utils';
import CustomAccordion from '../accordion/CustomAccordion';
import AccordionItem from '../accordion/AccordionItem';
import Loader from '../loader/Loader';

interface ISearchCategoryResultProps {
  items: SearchCategoryResultItem[];
}

function SearchCategoryResult({
  items,
}: ISearchCategoryResultProps): JSX.Element {
  const { t: translation } = useTranslation();
  const { data: contentTreeData, isFetching } = useGetApiCategoryTreeQuery();
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedCategoryItemInSearchParam = searchParams.get(
    selectedSearchResultCategory,
  );
  const [selectedCategoryItem, setSelectedCategoryItem] = useState(
    selectedCategoryItemInSearchParam ?? '',
  );

  const setSelectedCategoryItemAndUrlParam = (categoryId: string) => {
    setSelectedCategoryItem(categoryId);
    if (categoryId !== '-1') {
      searchParams.set(selectedSearchResultCategory, categoryId);
      setSearchParams(searchParams);
    } else {
      searchParams.delete(selectedSearchResultCategory);
      setSearchParams(searchParams);
    }
  };

  const getResultItemArticles = useCallback(
    (resultItem: SearchCategoryResultItem): JSX.Element => {
      const articles = _.keys(resultItem.articleIdsWithFoundTerms).map(
        (id) =>
          contentTreeData?.resultObject?.articles?.find((a) => a.id === id),
      ) as Article[];

      return (
        articles && (
          <CustomListGroup
            listItems={
              articles
                .filter((a) => a !== undefined)
                .map((a) => ({
                  id: a.id || '',
                  content: (
                    <div className='p-0'>
                      {a.recentVersionTitle || ''}
                      {resultItem.articleIdsWithFoundTerms && (
                        <span className='found-terms ms-1'>{`(${translation(
                          'foundTerms',
                        )}: ${
                          resultItem.articleIdsWithFoundTerms[a.id || '']
                        })`}</span>
                      )}
                    </div>
                  ),
                  href:
                    a.type !== ArticleType.StructureElement
                      ? getArticleUrl(
                          a.id || '',
                          undefined,
                          undefined,
                          undefined,
                          searchParams.get(searchKeywordUrlParam)?.split(',') ||
                            undefined,
                        )
                      : undefined,
                  iconClass: `${getArticleIcon(a.type || ArticleType.Html)}`,
                })) || []
            }
          />
        )
      );
    },
    [contentTreeData],
  );

  const resultItems = useMemo(
    () =>
      items.map((resultItem) => {
        const category = contentTreeData?.resultObject?.categories?.find(
          (c) => c.id === resultItem.categoryId,
        );
        const parentCategory = contentTreeData?.resultObject?.categories?.find(
          (c) => c.id === category?.parentId,
        );

        return {
          categoryId: resultItem.categoryId,
          title: (
            <>
              <h3 className='result-item-header text-body'>
                <i
                  className={`${category?.categoryTypeIconClass || ''}`}
                  style={
                    category?.categoryTypeColorHexCode
                      ? {
                          color: category?.categoryTypeColorHexCode || '',
                        }
                      : undefined
                  }
                  aria-hidden
                />
                <span className='mt-1'>
                  {category?.abbreviation ? `${category?.abbreviation}, ` : ''}
                  {parse(category?.name || '')}
                </span>
              </h3>
              <span className='text-muted fs-6 fst-italic'>
                ({parentCategory?.permittedPath || ''})
              </span>
            </>
          ),
          articles: getResultItemArticles(resultItem),
        };
      }),
    [items, contentTreeData],
  );

  useEffect(() => {
    if (selectedCategoryItem && contentTreeData && !isFetching) {
      const searchResultCategoryElement = document.getElementById(
        `${selectedCategoryItem}`,
      );

      if (searchResultCategoryElement) {
        searchResultCategoryElement.scrollIntoView({
          behavior: 'instant' as ScrollBehavior,
        });
        searchResultCategoryElement.focus();
      }
    }
  }, [contentTreeData, isFetching]);

  return (
    <>
      {(!contentTreeData || isFetching) && <Loader />}
      {contentTreeData && !isFetching && (
        <CustomAccordion
          defaultActiveKey={items[0]?.categoryId ?? ''}
          activeKey={selectedCategoryItem}>
          {resultItems.map((r) => (
            <AccordionItem
              key={r.categoryId}
              title={r.title}
              activeKey={selectedCategoryItem}
              setActiveKey={setSelectedCategoryItemAndUrlParam}
              eventKey={`searchResultItem-${r.categoryId}`}
              iconPlacement='start'>
              {r.articles}
            </AccordionItem>
          ))}
        </CustomAccordion>
      )}
    </>
  );
}

export default SearchCategoryResult;
